/**
 * usePaymentSession — polls GET /api/public/pay/success?session=<id>
 * until the payment status resolves out of 'pending', or until the poll
 * window expires (~30s).
 *
 * Webhook/redirect race: Stripe redirects the user immediately after checkout,
 * but the webhook that flips pending→succeeded can arrive 1-5s later. Polling
 * handles this transparently.
 *
 * Poll strategy:
 *   - Interval: every 2s while status is pending (or row not yet present).
 *   - Max window: 30s (15 attempts). After that, stay on the last known state.
 *   - 404 during the window = row not yet written = treat as still-pending.
 *   - Once succeeded or failed → stop polling (refetchInterval returns false).
 */

import { useQuery } from '@tanstack/react-query';
import { getPaymentSession, type PaymentSessionResult } from '@/services/publicPayApi';

export type PaymentSessionStatus =
  | { phase: 'loading' }
  | { phase: 'no-session' }
  | { phase: 'processing' } // pending or 404-during-window
  | { phase: 'timed-out' } // poll window elapsed, still pending
  | { phase: 'succeeded'; data: PaymentSessionResult }
  | { phase: 'failed'; data: PaymentSessionResult }
  | { phase: 'error' }; // network / unexpected error

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_MS = 30_000;

export function usePaymentSession(sessionId: string | undefined): PaymentSessionStatus {
  const startedAt = Date.now();

  const query = useQuery({
    queryKey: ['payment-session', sessionId],
    queryFn: () => getPaymentSession(sessionId!),
    enabled: !!sessionId,
    retry: false,
    staleTime: 0,
    refetchInterval: (query) => {
      const result = query.state.data;
      // Terminal: succeeded or failed → stop
      if (result?.ok && (result.data.status === 'succeeded' || result.data.status === 'failed')) {
        return false;
      }
      // Poll window elapsed → stop (caller reads timed-out state)
      if (Date.now() - startedAt >= POLL_MAX_MS) {
        return false;
      }
      // Still pending (ok result with pending status, or 404, or not yet fetched) → keep polling
      return POLL_INTERVAL_MS;
    },
  });

  if (!sessionId) return { phase: 'no-session' };
  if (query.isLoading || query.isPending) return { phase: 'loading' };

  const result = query.data;

  if (!result) {
    // Query ran but data is undefined (shouldn't happen with retry:false, but guard it)
    return query.isError ? { phase: 'error' } : { phase: 'loading' };
  }

  if (!result.ok) {
    // 404 = no Payment row yet during poll window → treat as processing
    if (result.status === 404) {
      const elapsed = Date.now() - startedAt;
      return elapsed >= POLL_MAX_MS ? { phase: 'timed-out' } : { phase: 'processing' };
    }
    // Network failure or other server error
    return { phase: 'error' };
  }

  const { status } = result.data;

  if (status === 'succeeded') return { phase: 'succeeded', data: result.data };
  if (status === 'failed') return { phase: 'failed', data: result.data };

  // pending or refunded (edge case) — check if poll window elapsed
  const elapsed = Date.now() - startedAt;
  return elapsed >= POLL_MAX_MS ? { phase: 'timed-out' } : { phase: 'processing' };
}
